import React from 'react';

import { useMutation, useQuery, useQueryClient } from '@lumapps/base-api/react-query';
import { useNotification } from '@lumapps/notifications/hooks/useNotifications';
import { fetchToggleReaction, getReactionStatus } from '@lumapps/reactions-core/api';
import { reactionsQueryKeys } from '@lumapps/reactions-core/api/query';
import { ReactionState, ResourceRef } from '@lumapps/reactions-core/api/types';
import type { ReactionResourceType, ReactionType } from '@lumapps/reactions-core/api/types';
import { updateReactionState } from '@lumapps/reactions-core/utils/updateReactionState';
import { GLOBAL } from '@lumapps/translations';

/**
 * Get or fetch reaction status and toggle reaction.
 */
export const useResourceReaction = ({
    resourceRef,
    resourceState,
    isInitialStateStale = false,
}: {
    resourceRef: ResourceRef<ReactionResourceType>;
    resourceState: ReactionState;
    isInitialStateStale?: boolean;
}) => {
    const notify = useNotification();

    // Get or fetch reaction status (if fetch function provided, and data is stale)
    const { isLoading, data } = useQuery({
        enabled: isInitialStateStale && !!getReactionStatus && !!resourceRef.resourceId,
        queryKey: reactionsQueryKeys.reactionStatus(resourceRef),
        queryFn: ({ signal }) => getReactionStatus?.(resourceRef, signal),
        initialData: resourceState,
    });

    const state = data || resourceState;

    const queryClient = useQueryClient();

    const stateBeforeToggleRef = React.useRef(state);

    // Toggle add/remove reaction
    const { mutate, isLoading: toggleReactionLoading } = useMutation({
        mutationFn: (reaction: ReactionType) =>
            fetchToggleReaction(resourceRef, reaction, state.reactionCounts, state.currentUserReactions),
        mutationKey: reactionsQueryKeys.reactionStatus(resourceRef),
        onMutate(reaction) {
            // Store previous state to rollback in case of error.
            stateBeforeToggleRef.current = state;

            // Optimistic update
            queryClient.setQueryData(
                reactionsQueryKeys.reactionStatus(resourceRef),
                updateReactionState(state, reaction),
            );
        },
        onSuccess(newState) {
            // Store in cache
            queryClient.setQueryData(reactionsQueryKeys.reactionStatus(resourceRef), newState);
        },
        onError() {
            // Generic error notification
            notify.error({ translate: GLOBAL.GENERIC_ERROR });
            // Rollback optimistic update
            queryClient.setQueryData(reactionsQueryKeys.reactionStatus(resourceRef), stateBeforeToggleRef.current);
        },
        onSettled() {
            // Invalidate reaction list query
            const queryKey = reactionsQueryKeys.listReactions(resourceRef).filter(Boolean);
            queryClient.invalidateQueries({ queryKey });
        },
    });

    return {
        reactionCounts: state.reactionCounts,
        currentUserReactions: state.currentUserReactions,
        toggleReaction: mutate,
        isLoading: isLoading || toggleReactionLoading,
    };
};
